/**
 * What the plan's researching face shows while nobody is watching it. F-141.
 *
 * **The wait is not a loading screen for the plan. It is the first thing the product does for
 * somebody**, and this module is the part of it a test can see. Leo, on what it is for:
 *
 * > *"the goal is NOT to provide an accurate plan nor to start asking questions… the objective
 * > is not to make the user act on an incomplete plan, rather to demonstrate to the user, in a
 * > valuable way, that research is ongoing and maintain engagement."*
 *
 * So nothing here builds anything, proposes anything or asks anything. It is a display, and
 * every card in it is text some pass already wrote and was already paid for: the outline's
 * titles, abstracts and `highlights` — all twelve of which exist about forty seconds in, before
 * any research runs — then, as subjects settle, a paragraph of what was actually found.
 *
 * **Every card is addressed to the learner**, which F-158 had to make true rather than assume. A
 * fourth kind showed the row's `missing`, and `missing` is a note to whoever builds the
 * curriculum: what reached a learner's plan screen was *"Scope warning for whoever builds the
 * curriculum from this: the web tooling stopped returning results after a single query"*. See
 * `CardKind`.
 *
 * **It loops**, which is what makes it a display rather than a programme:
 *
 * > *"the carousel can loop… if they ever go back to the plan's home, they will see moving facts
 * > and content relevant to their plan."*
 *
 * There is no "fill sixteen minutes" problem, because nobody is asked to watch for sixteen
 * minutes — they are elsewhere in the product, or away, and this is what greets them when they
 * look. That is also why it is a loop and not a queue: the deck an outline of twelve subjects
 * produces is forty-eight cards — an abstract and three "what you will learn" lines each — at
 * three to nine seconds apiece, which is what a first few minutes of attention costs rather than
 * a budget for a wait.
 *
 * **Its own module with no DOM and no React in it, for `plan/view.ts`'s reason and one more.**
 * `npm test` does not type-check `.tsx` at all, so a rotation written inline in a component is
 * a rotation no test in this repository can see — and the two things most worth asserting here
 * are exactly the ones nobody would report: what it chooses to show next, and whether it moves
 * at all for somebody who asked the operating system for less motion.
 */

/*
 * Types only, so nothing from `@nabu/shared` reaches the bundle or the Node test's runtime —
 * `plan/pulse.ts` explains why that matters, and it is the same barrel with the same
 * `node:crypto` behind it.
 */
import type { ResearchRunState, ResearchSubject } from '@nabu/shared';

/**
 * What one card is made of.
 *
 * Three kinds rather than one, because the difference is what the learner is being shown and it
 * decides how the card is labelled. `outline` and `promise` both come from the outline and are
 * separated because one is a description of a subject and the other is addressed to the reader;
 * `finding` is the research itself.
 *
 * **A fourth kind, `gap`, was here until F-158, and its absence is the deletion.** It showed the
 * subject row's `missing` — what a pass could not retrieve — on the reasoning that an honest gap
 * is more engaging than silence. What reached the learner was not that. Leo, 2026-09-19, reading
 * his own plan's carousel:
 *
 * > *"showing stuff like this in the carousel: 'Could not retrieve: (1) cortisol's circadian
 * > curve… Scope warning for whoever builds the curriculum from this: the web tooling stopped
 * > returning results after a single query…'"*
 *
 * `missing` is written by a research pass **for whoever builds the curriculum from it** — it says
 * so in its own text — and it is now operator-only: the row keeps it, the console and the
 * coverage report read it, and nothing learner-facing renders it. See `deckOf`, which no longer
 * has a branch for it, and `Research.tsx`, which no longer prints it under a subject.
 */
export type CardKind = 'outline' | 'promise' | 'finding';

export interface Card {
  /**
   * What "already shown" is remembered by, and it has to survive a poll.
   *
   * The deck is rebuilt from scratch every time the progress endpoint answers — every five
   * seconds — so an index would be meaningless and an object identity would be new each time.
   * The key is derived from the subject and the text, which means a card whose text changed is
   * a new card and gets its turn, and a card that merely moved does not lose its place.
   */
  key: string;
  kind: CardKind;
  subjectId: string;
  /** The subject this is about, for the label above the card. */
  title: string;
  text: string;
  /** Where this subject comes in the teaching order, counting from one. */
  place: number;
  /** How many subjects there are, so the label can say "3 of 12" without a second count. */
  subjects: number;
  /**
   * How many sources this subject's research rests on, on a `finding` card only. F-158.
   *
   * **Two fields were here until F-158 and this replaces both**: `characters`, the count of
   * material written, and a `sources` *string* parsed out of the document's origin — "5
   * searches". Neither is what the owner asked the card to say. A character count is a number
   * nobody can judge (*"number of characters is quite irrelevant, can we do number of
   * sources?"*), and a count of searches is a fact about our tooling rather than about the
   * subject. This is the source count the pass itself reported, stored on the row since F-158.
   *
   * Absent when the row predates the counter, which is every row in the table on the day it
   * ships — the card then says where it is from and nothing more, rather than claiming nought.
   */
  sources?: number;
}

/**
 * A paragraph of a subject's research, as `GET /api/research/snippet` answers it.
 *
 * Declared structurally rather than imported from `api.ts`, which reaches for `fetch` and a
 * session at module scope and would drag both into a Node test for the sake of one shape.
 */
export interface Finding {
  subjectId: string;
  snippet?: string;
}

/**
 * The teaching order, restated here because the carousel is the one screen that must not get
 * it wrong.
 *
 * The API sorts by `ordinal` and `plan/coverage.ts` deliberately does not sort again. This does,
 * and the reason is that it reads from two sources: the subjects as the API returned them, and
 * the findings, which arrive in whatever order twelve passes happened to settle. A learner
 * meeting their objective for the first time should meet it at chapter one, and F-110 exists
 * because a random sort key had them meeting it at chapter seven.
 *
 * Rows written before F-110 have no ordinal and sort after the ones that do, by subject id —
 * the same total, stable order `readSubjects` uses in the pipeline.
 */
function inTeachingOrder(subjects: readonly ResearchSubject[]): ResearchSubject[] {
  return [...subjects].sort((left, right) => {
    const a = left.ordinal;
    const b = right.ordinal;
    if (a !== undefined && b !== undefined) return a - b;
    if (a !== undefined) return -1;
    if (b !== undefined) return 1;
    return left.subjectId.localeCompare(right.subjectId);
  });
}

/** Cards no shorter than this. A fragment on screen for three seconds is a rendering fault. */
const MIN_CARD_CHARS = 12;

/**
 * And no longer than this, which is what stops the panel resizing under a thumb.
 *
 * **A review caught the panel jumping, and the arithmetic is worth writing down because the
 * first version of it was wrong.** At `1rem`/`1.5` line height inside the carousel's padding, a
 * 360-pixel phone fits roughly 35 characters to a line. The outline caps an abstract at 400
 * characters and the snippet route caps a research paragraph at 360, so an uncapped card runs to
 * ten or eleven lines — against a floor sized for five. The panel therefore grew and shrank on
 * most transitions and took the controls with it, on the one screen that is meant to feel calm
 * and on the device most of these learners are holding.
 *
 * 240 characters is about seven lines, which the CSS floor covers. A `finding` is the only kind
 * that regularly reaches it, and `trimTo` cuts it at a sentence end rather than mid-word, so
 * what is lost is the tail of a paragraph the learner can read in full on the research section
 * of the same screen.
 */
const MAX_CARD_CHARS = 240;

/**
 * A card's text, cut to a length that fits without cutting a word in half.
 *
 * Prefers the last sentence end inside the limit, falls back to the last space, and adds an
 * ellipsis only when something was actually removed. A bare `slice` would end cards mid-word,
 * which reads as a rendering fault rather than as a quotation.
 */
function trimTo(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const head = text.slice(0, limit);
  // Far enough in that a trim to the first clause does not leave a fragment.
  const sentence = Math.max(head.lastIndexOf('. '), head.lastIndexOf('; '));
  if (sentence > limit * 0.6) return head.slice(0, sentence + 1);
  /*
   * The ellipsis is inside the limit, not added to it, which the first version of this got
   * wrong: a 400-character run of one word has no space to cut at, so `head` came back whole and
   * the ellipsis made it one character over the bound the test asserts. A cap that the cap's own
   * fallback can exceed is not a cap.
   */
  const room = text.slice(0, limit - 1);
  const space = room.lastIndexOf(' ');
  return `${(space > 0 ? room.slice(0, space) : room).trimEnd()}…`;
}

/**
 * Everything there is to show right now, in the order it was written.
 *
 * **Rebuilt from the reading rather than accumulated**, which is what makes "upgrades in place"
 * work without any upgrading: a subject that has settled since the last poll simply has more
 * cards this time, and because the rotation prefers what it has not shown, those are the ones
 * it reaches next. Nothing has to be told that a subject changed.
 *
 * Grouped by subject rather than interleaved, so what a learner sees is a subject and then what
 * they will get out of it, and not three sentences about three different things. Within a
 * subject the outline comes first because it is what existed first; the research and the gap
 * are appended as they arrive, which is where the rotation finds them.
 */
export function deckOf(input: {
  subjects: readonly ResearchSubject[];
  /** What has been fetched so far. Empty until the first subject settles. */
  findings: readonly Finding[];
}): Card[] {
  const ordered = inTeachingOrder(input.subjects);
  const cards: Card[] = [];

  ordered.forEach((subject, index) => {
    const place = index + 1;
    const shared = { subjectId: subject.subjectId, title: subject.title, place, subjects: ordered.length };

    const add = (kind: CardKind, text: string, extra?: Partial<Card>): void => {
      const clean = text.trim();
      /*
       * A card with nothing in it is dropped rather than rendered empty, and this is the guard
       * that matters most: an outline row written before F-141 has no highlights, a subject that
       * has not settled has no finding, and half the rows in the live table have an abstract and
       * nothing else. Every one of those paths arrives here, and the failure they would produce
       * is three seconds of blank panel that nobody would file a bug about.
       */
      if (clean.length < MIN_CARD_CHARS) return;
      cards.push({
        // Keyed on the untrimmed text, so a subject re-researched into a longer paragraph with
        // the same opening is still a new card rather than one the rotation thinks it has shown.
        key: `${subject.subjectId}:${kind}:${clean.slice(0, 64)}`,
        kind,
        text: trimTo(clean, MAX_CARD_CHARS),
        ...shared,
        ...extra,
      });
    };

    add('outline', subject.abstract);
    for (const line of subject.highlights ?? []) add('promise', line);

    const finding = input.findings.find((entry) => entry.subjectId === subject.subjectId);
    if (finding?.snippet) {
      /*
       * `typeof` rather than a truthiness test, so a subject that read **nought** sources says so
       * rather than falling through to saying nothing. The two are different facts and the row
       * distinguishes them; see `ResearchSubject.sources`.
       */
      add('finding', finding.snippet, {
        ...(typeof subject.sources === 'number' ? { sources: subject.sources } : {}),
      });
    }

    /*
     * **A `gap` card was added here until F-158.** See `CardKind`: `missing` is written for
     * whoever builds the curriculum out of a pass, and it reached a learner's carousel as a
     * scope warning addressed to somebody else. The row still carries it, for the console and
     * the coverage report; no screen renders it.
     */
  });

  return cards;
}

/**
 * Whether there is a carousel on this screen at all.
 *
 * **It needs an ending, and this is it.** A learner who left at minute two and comes back at
 * minute twenty to a finished plan must not meet a thing still insisting work is under way: the
 * carousel exists to say research is happening, so when it is not happening the carousel is not
 * a decoration, it is a lie that moves. The panel around it goes on showing what was found —
 * the counts, the curve, the quotations — which is the settled form of the same material.
 *
 * **It is `researchStillComing` in `@nabu/shared` restated, and that is deliberate**, for the
 * reason the import at the top of this file gives: the shared barrel re-exports `keys.ts`, which
 * imports `node:crypto`, so a *value* import from it would pull that into a browser bundle and
 * into a Node test that has no business having it. `plan/pulse.ts` carries its own copy of the
 * same sentence for the same reason. What makes it safe is that the type is imported, so a run
 * state added to the union without a home here fails the build.
 *
 * Keyed on the run's own state rather than on a staleness window, for `researchFace`'s reason:
 * nothing here withholds anything from the learner, so a display that decided on its own that a
 * quiet run had stopped would be this module inventing a claim the pipeline has not made.
 */
export function carouselShows(input: {
  run: ResearchRunState | undefined;
  /** How many cards there are. A carousel of nothing is a bordered empty box. */
  cards: number;
}): boolean {
  if (input.cards === 0) return false;
  return input.run === 'outlining' || input.run === 'deepening';
}

/* ------------------------------------------------------------------------- *
 * What to show next
 * ------------------------------------------------------------------------- */

/**
 * Which card is showing, and which have been shown.
 *
 * By key rather than by index, because the deck is rebuilt on every poll and a subject settling
 * inserts cards ahead of the current one. An index would slide the learner backwards mid-read
 * every time a finding landed.
 */
export interface Rotation {
  /** The card on screen, or nothing before the first one is chosen. */
  key?: string;
  /** Every key shown in the current pass through the deck. */
  seen: readonly string[];
}

export const START: Rotation = { seen: [] };

/**
 * The next card, preferring one the learner has not seen.
 *
 * **"Prefer unseen" is the whole engagement argument in one function.** A round-robin over
 * twelve abstracts repeats in under a minute, and a learner who returns at minute nine to the
 * same three sentences they saw at minute one has been shown that nothing happened — which is
 * the opposite of what this screen is for, and it would be shown at exactly the moment the most
 * new material exists. So: scan forward from where we are, wrap, and take the first card that
 * has not had its turn this pass.
 *
 * When everything has had its turn the pass ends and a new one begins — `seen` resets to the
 * one card just chosen, so the preference starts working again immediately and anything that
 * landed during the last pass is unseen once more. It does *not* stop: it loops, by design.
 *
 * Cards that have left the deck are dropped from `seen` on the way out. Without that, a set
 * that only grows would eventually hold a key for every text any subject ever had, which on a
 * two-hour run with re-researched subjects is a memory leak with a very slow fuse.
 */
export function nextCard(deck: readonly Card[], from: Rotation): Rotation {
  if (deck.length === 0) return START;

  const live = new Set(deck.map((card) => card.key));
  const seen = new Set(from.seen.filter((key) => live.has(key)));
  const at = deck.findIndex((card) => card.key === from.key);

  for (let step = 1; step <= deck.length; step++) {
    const card = deck[(at + step + deck.length * 2) % deck.length];
    if (!card || seen.has(card.key)) continue;
    seen.add(card.key);
    return { key: card.key, seen: [...seen] };
  }

  /*
   * Every card has had its turn. Round again from the next one, with a fresh memory.
   *
   * `at + 1` rather than 0, so a loop does not visibly snap back to the first subject every
   * time it completes — it carries on past the end into the beginning, which is what a carousel
   * looks like when it is working.
   */
  const card = deck[(at + 1 + deck.length * 2) % deck.length];
  return card ? { key: card.key, seen: [card.key] } : START;
}

/**
 * One step by hand, forwards or back, for the buttons and the arrow keys.
 *
 * **It marks what it lands on as seen, and that is not an oversight.** A learner paging back
 * through what they just read has read it; leaving those unseen would mean the automatic
 * rotation immediately replayed the same cards when they let go of it.
 */
export function stepCard(deck: readonly Card[], from: Rotation, delta: number): Rotation {
  if (deck.length === 0) return START;
  const live = new Set(deck.map((card) => card.key));
  const seen = new Set(from.seen.filter((key) => live.has(key)));
  const at = deck.findIndex((card) => card.key === from.key);
  // `at` is -1 when nothing is showing yet, which makes a forward step land on the first card.
  const card = deck[((at + delta) % deck.length + deck.length) % deck.length];
  if (!card) return START;
  seen.add(card.key);
  return { key: card.key, seen: [...seen] };
}

/** Where the current card is in the deck, counting from one, or nought if it has gone. */
export function positionOf(deck: readonly Card[], rotation: Rotation): number {
  return deck.findIndex((card) => card.key === rotation.key) + 1;
}

/* ------------------------------------------------------------------------- *
 * How long, and whether at all
 * ------------------------------------------------------------------------- */

/**
 * The floor, and it is Leo's number.
 *
 * > *"if each snippet is shown for 3 seconds, and there's 12 subjects, and each subject has 3
 * > snippets, that's already 108 seconds of information."*
 *
 * Three seconds is right for the thing that arithmetic was about — a short "what you will
 * learn" line, capped at 160 characters by the outline. It is not right for a 400-character
 * abstract or a paragraph lifted out of a research document, and a carousel that takes a
 * paragraph away before it can be read is worse than one that never showed it.
 */
export const MIN_DWELL_MS = 3_000;

/**
 * The ceiling, which is where a carousel stops reading as one.
 *
 * Nine seconds is already a long time to look at a panel that is supposed to be moving. A card
 * that would want longer than this is a card the learner will finish reading on its next turn,
 * and the loop guarantees there is one.
 */
export const MAX_DWELL_MS = 9_000;

/**
 * Roughly how long a character takes to read, on a phone, glancing.
 *
 * About 45ms, which is a bit over 200 words a minute — ordinary adult reading speed rather than
 * a skim, because the person this screen exists for has never seen any of these words before
 * and is deciding whether this product is worth waiting for.
 */
const MS_PER_CHAR = 45;

/** How long this card stays up. */
export function dwellMs(card: Card | undefined): number {
  if (!card) return MIN_DWELL_MS;
  return Math.min(MAX_DWELL_MS, Math.max(MIN_DWELL_MS, Math.round(card.text.length * MS_PER_CHAR)));
}

/**
 * Whether the carousel moves on its own.
 *
 * **`prefers-reduced-motion` matters here more than anywhere else in the app, because this
 * thing moves on its own**, and `styles.css` cannot help: its blanket rule collapses animations
 * and transitions to 0.01ms, which does nothing at all to a `setTimeout` that swaps the text.
 * A learner who asked the operating system for less motion and got an automatic slideshow has
 * been told the setting is decorative. So the answer is not "animate the change less" — it is
 * that the carousel does not advance at all, and the buttons that were always there become the
 * only way it moves. Everything is still reachable; nothing moves unasked.
 *
 * `paused` is the learner's own doing — the pause button, a pointer over the panel, or the
 * keyboard focus being inside it, which is the same event for somebody who cannot use a mouse.
 *
 * `hidden` is `document.visibilityState`, and it is here rather than in the component because
 * it is a correctness rule and not an optimisation: a background tab that kept rotating would
 * mark every card seen while nobody was looking, so the learner coming back — the exact person
 * this feature is for — would return to a deck with nothing unseen left in it.
 */
export function autoAdvances(input: {
  reducedMotion: boolean;
  paused: boolean;
  hidden: boolean;
}): boolean {
  return !input.reducedMotion && !input.paused && !input.hidden;
}

/**
 * What a screen reader is told, and when.
 *
 * **An `aria-live` region that fires every three seconds is hostile.** It interrupts whatever
 * the learner is reading, it cannot be paused by the reader's own controls, and it does it
 * thirty-six times a minute with text nobody asked for. A non-sighted learner on this screen
 * wants what everybody else wants — to know research is under way, and to be able to look at
 * what has been found — and neither of those needs an announcement on a timer.
 *
 * So the region is `off` while the carousel rotates by itself, and `polite` the moment it stops
 * rotating: paused, hovered, focused, or not rotating at all because the learner asked for less
 * motion. That is the APG carousel rule and it has a good reason behind it — when the learner is
 * the one moving it, the new card is the answer to something they just did, and announcing it is
 * the only way they know it worked.
 *
 * The consequence worth stating: a screen-reader user who tabs into the carousel stops it, and
 * from then on it is a three-button reader they drive themselves. The cards are all in the DOM
 * and reachable either way; what changes is whether anything speaks without being asked.
 */
export function liveness(input: { reducedMotion: boolean; paused: boolean }): 'off' | 'polite' {
  return input.reducedMotion || input.paused ? 'polite' : 'off';
}
